// src/components/KenterSyncNotification.js - Enhanced with ISTA Weekly Usage
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import api from './api';
import '../css/KenterSyncNotification.css';

/**
 * Smart notification component that appears when it's time to sync energy data
 * Now syncs both Kenter data AND ISTA weekly usage data
 * Shows on the 5th of each month (or after) if sync hasn't been done this month
 */
const KenterSyncNotification = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSyncDate, setLastSyncDate] = useState(null);
  const [lastIstaSyncDate, setLastIstaSyncDate] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [syncLog, setSyncLog] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  // Check if we should show the notification
  useEffect(() => {
    checkSyncStatus();
  }, []);

  const checkSyncStatus = async () => {
    try {
      // Get last sync dates from both APIs
      const [kenterResponse, istaResponse] = await Promise.all([
        api.get('/api/kenter/last-update').catch(() => ({ data: null })),
        api.get('/api/ista/last-date').catch(() => ({ data: null }))
      ]);

      const kenterLastUpdate = kenterResponse.data?.lastUpdate;
      const istaLastUpdate = istaResponse.data?.lastDate;
      
      if (kenterLastUpdate) {
        setLastSyncDate(dayjs(kenterLastUpdate));
      }
      
      if (istaLastUpdate) {
        setLastIstaSyncDate(dayjs(istaLastUpdate));
      }

      // Use the older of the two dates to determine if sync is needed
      // This ensures we sync if EITHER system needs updating
      let oldestUpdate = null;
      if (kenterLastUpdate && istaLastUpdate) {
        oldestUpdate = dayjs(kenterLastUpdate).isBefore(dayjs(istaLastUpdate)) 
          ? kenterLastUpdate 
          : istaLastUpdate;
      } else if (kenterLastUpdate) {
        oldestUpdate = kenterLastUpdate;
      } else if (istaLastUpdate) {
        oldestUpdate = istaLastUpdate;
      }

      // Check if notification should be shown
      const shouldShow = shouldShowSyncNotification(oldestUpdate);
      setShowNotification(shouldShow);

      // Check if user has dismissed it today
      const dismissedToday = localStorage.getItem('energy-sync-dismissed');
      if (dismissedToday === dayjs().format('YYYY-MM-DD')) {
        setIsDismissed(true);
      }

    } catch (error) {
      console.error('Error checking sync status:', error);
      // If we can't check, assume we should show notification on 5th or later
      const today = dayjs();
      if (today.date() >= 5) {
        setShowNotification(true);
      }
    }
  };

  const shouldShowSyncNotification = (lastUpdateStr) => {
    const today = dayjs();
    const currentMonth = today.month();
    const currentYear = today.year();
    const dayOfMonth = today.date();

    // Only show on or after the 5th of the month
    if (dayOfMonth < 5) {
      return false;
    }

    // If no last update, definitely show
    if (!lastUpdateStr) {
      return true;
    }

    const lastUpdate = dayjs(lastUpdateStr);
    const lastUpdateMonth = lastUpdate.month();
    const lastUpdateYear = lastUpdate.year();

    // Show if last sync was not in the current month
    return !(lastUpdateMonth === currentMonth && lastUpdateYear === currentYear);
  };

  const handleSync = async () => {
    setIsLoading(true);
    setSyncLog('');
    setShowDetails(true);

    try {
      setSyncLog('🚀 Starting comprehensive energy data sync...\n\n');
      
      // 1. Sync Kenter data
      setSyncLog(prev => prev + '📊 Step 1: Syncing Kenter API data...\n');
      const kenterResponse = await api.get('/api/kenter/sync', {
        params: { months: 12 }
      });
      
      setSyncLog(prev => prev + '✅ Kenter sync completed successfully\n');
      setSyncLog(prev => prev + `   Stats: ${kenterResponse.data?.stats?.monthsProcessed || 0} months, ${kenterResponse.data?.stats?.totalImported || 0} records\n\n`);
      
      // 2. Sync ISTA Weekly Usage
      setSyncLog(prev => prev + '🏢 Step 2: Syncing ISTA weekly usage data...\n');
      
      // Calculate date range for ISTA sync (last 3 months)
      const today = dayjs();
      const threeMonthsAgo = today.subtract(3, 'month');
      const fromDate = threeMonthsAgo.format('YYYY-MM-DD');
      const toDate = today.format('YYYY-MM-DD');
      
      setSyncLog(prev => prev + `   Date range: ${fromDate} to ${toDate}\n`);
      
      try {
        const istaResponse = await api.get('/api/ista/weekly-usage', {
          params: { 
            from: fromDate, 
            to: toDate 
          }
        });
        
        setSyncLog(prev => prev + '✅ ISTA weekly usage sync completed successfully\n');
        setSyncLog(prev => prev + `   Result: ${istaResponse.data || 'Weekly usage data updated'}\n\n`);
      } catch (istaError) {
        // ISTA sync can fail if recent data isn't available yet - this is normal
        if (istaError.response?.status === 400) {
          setSyncLog(prev => prev + '⚠️ ISTA sync completed with partial data (recent weeks not yet available)\n');
          setSyncLog(prev => prev + '   This is normal - ISTA updates data with a delay\n');
          setSyncLog(prev => prev + '   All available data has been successfully imported\n\n');
        } else {
          // Re-throw if it's a different kind of error
          throw istaError;
        }
      }
      
      // 3. Sync Degree Days
      setSyncLog(prev => prev + '🌡️ Step 3: Syncing degree days data...\n');
      
      // Sync degree days (last 6 months to day before yesterday)
      const sixMonthsAgo = dayjs().subtract(6, 'month').format('YYYY-MM-DD');
      const twoDaysAgo = dayjs().subtract(2, 'day').format('YYYY-MM-DD');
      
      setSyncLog(prev => prev + `   Date range: ${sixMonthsAgo} to ${twoDaysAgo}\n`);
      
      const degreeDaysResponse = await api.get('/api/degree-days/sync-archive', {
        params: {
          start: sixMonthsAgo,
          end: twoDaysAgo
        }
      });
      
      setSyncLog(prev => prev + '✅ Degree Days sync completed successfully\n');
      setSyncLog(prev => prev + `   Stats: ${degreeDaysResponse.data?.totalProcessed || 0} days processed\n\n`);
      
      setSyncLog(prev => prev + '🎉 ALL SYNCS COMPLETED!\n');
      setSyncLog(prev => prev + '📈 Your energy data is now up to date with:\n');
      setSyncLog(prev => prev + '   • Latest available Kenter energy usage data\n');
      setSyncLog(prev => prev + '   • Most recent ISTA weekly usage data (up to what\'s available)\n');
      setSyncLog(prev => prev + '   • Current degree days for efficiency calculations\n');
      setSyncLog(prev => prev + '\n💡 Note: ISTA data may have a delay of 1-2 weeks - this is normal.\n');
      
      setSyncStatus('success');
      
      // Update last sync date
      await checkSyncStatus();
      
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncLog(prev => prev + `\n❌ SYNC FAILED: ${error.message}\n`);
      setSyncLog(prev => prev + `Details: ${error.response?.data?.error || error.response?.data?.message || 'Unknown error'}\n`);
      setSyncLog(prev => prev + '\nPlease try again or contact support if the problem persists.\n');
      setSyncStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    // Remember dismissal for today only
    localStorage.setItem('energy-sync-dismissed', dayjs().format('YYYY-MM-DD'));
    setIsDismissed(true);
  };

  const formatLastSyncDate = (syncDate, label) => {
    if (!syncDate) return `${label}: Nooit`;
    
    const daysAgo = dayjs().diff(syncDate, 'day');
    const formatted = syncDate.format('DD-MM-YYYY HH:mm');
    
    if (daysAgo === 0) return `${label}: Vandaag (${formatted})`;
    if (daysAgo === 1) return `${label}: Gisteren (${formatted})`;
    return `${label}: ${daysAgo} dagen geleden (${formatted})`;
  };

  const getNotificationPriority = () => {
    const today = dayjs();
    const dayOfMonth = today.date();
    
    if (dayOfMonth >= 15) return 'high'; // Very late
    if (dayOfMonth >= 10) return 'medium'; // Getting late
    return 'normal'; // On time
  };

  const getNotificationMessage = () => {
    const today = dayjs();
    const dayOfMonth = today.date();
    const priority = getNotificationPriority();
    
    if (priority === 'high') {
      return `Het is al ${dayOfMonth} ${today.format('MMMM')} - tijd om alle energiegegevens te synchroniseren!`;
    } else if (priority === 'medium') {
      return `Reminder: Maandelijkse energiegegevens synchronisatie voor ${today.format('MMMM')}`;
    } else {
      return `Tijd voor de maandelijkse energiegegevens synchronisatie`;
    }
  };

  // Don't render if conditions aren't met
  if (!showNotification || isDismissed) {
    return null;
  }

  const priority = getNotificationPriority();

  return (
    <div className={`kenter-sync-notification ${priority}`}>
      <div className="notification-header">
        <div className="notification-icon">
          {priority === 'high' ? '🚨' : priority === 'medium' ? '⚠️' : '📊'}
        </div>
        <div className="notification-content">
          <h3>{getNotificationMessage()}</h3>
          <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
            <p>{formatLastSyncDate(lastSyncDate, 'Kenter')}</p>
            <p>{formatLastSyncDate(lastIstaSyncDate, 'ISTA')}</p>
          </div>
          <p className="notification-description">
            Synchroniseert Kenter data, ISTA weekly usage en graaddagen in één keer.
          </p>
        </div>
        <div className="notification-actions">
          <button
            onClick={handleSync}
            disabled={isLoading}
            className="sync-button primary"
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Bezig...
              </>
            ) : (
              <>
                🔄 Alles Synchroniseren
              </>
            )}
          </button>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="sync-button secondary"
            disabled={isLoading}
          >
            {showDetails ? 'Verberg Details' : 'Toon Details'}
          </button>
          <button
            onClick={handleDismiss}
            className="dismiss-button"
            title="Verberg voor vandaag"
          >
            ✕
          </button>
        </div>
      </div>

      {showDetails && (
        <div className="notification-details">
          <div className="sync-info">
            <h4>📋 Wat wordt gesynchroniseerd:</h4>
            <ul>
              <li>🏢 <strong>Kenter API:</strong> Laatste 12 maanden energieverbruik van het hele gebouw</li>
              <li>🏠 <strong>ISTA Weekly Usage:</strong> Laatste 3 maanden individuele appartementen data (tot beschikbare data)</li>
              <li>🌡️ <strong>Graaddagen:</strong> Laatste 6 maanden weerdata voor efficiëntie berekeningen</li>
              <li>⏱️ <strong>Totale duur:</strong> Meestal 3-7 minuten</li>
              <li>ℹ️ <strong>Let op:</strong> ISTA data kan 1-2 weken vertraging hebben</li>
            </ul>
          </div>

          {syncLog && (
            <div className="sync-log">
              <h4>📝 Sync Log:</h4>
              <div className={`log-container ${syncStatus}`}>
                <pre>{syncLog}</pre>
              </div>
            </div>
          )}

          {syncStatus && (
            <div className={`sync-result ${syncStatus}`}>
              {syncStatus === 'success' ? (
                <div className="success-message">
                  <span className="icon">✅</span>
                  <span>Alle synchronisaties succesvol voltooid! Je energiegegevens zijn nu volledig up-to-date.</span>
                </div>
              ) : (
                <div className="error-message">
                  <span className="icon">❌</span>
                  <span>Een of meer synchronisaties zijn mislukt. Controleer de log voor details.</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KenterSyncNotification;